import { Circle } from '@react-google-maps/api';
import { useEffect, useMemo, useState } from 'react';

import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { getLocationHistoriesByMovingSite } from '~hooks/useLocationHistory/useLocationHistory';
import {
  type JobSiteUsage,
  type LoadSiteUsage,
  type OrderSiteUsage,
} from '~hooks/useSiteUsages/models';

import { buildTimedPath, interpolatePositionAtTime } from './catmullRomSpline';
import DistanceRouteMap, { MapLoader } from './DistanceRouteMap';
import { MOVING_SITE_CIRCLE_OPTIONS } from './timelineTicks';
import WaypointMarkers, { buildWaypointPoints } from './WaypointMarkers';

interface Props {
  companyId: string;
  /** only needed for jobs (moving site fetch requires a job ID). */
  jobId?: string;
  siteUsages: (JobSiteUsage | OrderSiteUsage | LoadSiteUsage)[];
  pings: LocationHistoryMinimal[];
  isLoading: boolean;
}

const DistanceMapEntity = ({ companyId, jobId, siteUsages, pings, isLoading }: Props) => {
  const [indicatorTime, setIndicatorTime] = useState<number | null>(null);
  const [movingSitePings, setMovingSitePings] = useState<LocationHistoryMinimal[]>([]);

  const movingSiteInfo = useMemo(() => {
    for (let i = 0; i < siteUsages.length; i++) {
      const su = siteUsages[i];
      const movingSite = su.site.movingSite;
      if (!movingSite?.id) continue;
      const primary = su.site.nextBillionGeofence;
      const movingGeofence =
        (primary?.movingGeofence ? primary : null) ??
        su.additionalGeofences.find((g) => g.movingGeofence);
      return {
        siteIndex: i,
        movingSiteId: movingSite.id,
        radius: movingGeofence?.circleRadius ?? null,
      };
    }
    return null;
  }, [siteUsages]);

  useEffect(() => {
    if (!companyId || !jobId || !movingSiteInfo) {
      setMovingSitePings([]);
      return;
    }
    const controller = new AbortController();
    getLocationHistoriesByMovingSite(
      companyId,
      jobId,
      movingSiteInfo.movingSiteId,
      controller.signal,
    )
      .then((result) => {
        if (!controller.signal.aborted) setMovingSitePings(result);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [companyId, jobId, movingSiteInfo]);

  const movingSiteTimedPath = useMemo(
    () => buildTimedPath(movingSitePings),
    [movingSitePings],
  );

  const movingSitePosition = useMemo(() => {
    if (indicatorTime === null || !movingSiteInfo) return null;
    const { rawPath, pingTimes } = movingSiteTimedPath;
    if (rawPath.length === 0) return null;
    const clampedTime = Math.max(
      pingTimes[0],
      Math.min(indicatorTime, pingTimes[pingTimes.length - 1]),
    );
    const result = interpolatePositionAtTime(movingSiteTimedPath, clampedTime);
    if (!result) return null;
    return { position: result.position, radius: movingSiteInfo.radius };
  }, [indicatorTime, movingSiteTimedPath, movingSiteInfo]);

  const waypointPoints = useMemo(
    () => buildWaypointPoints(siteUsages, movingSiteInfo, movingSitePosition),
    [siteUsages, movingSiteInfo, movingSitePosition],
  );

  if (isLoading) {
    return <MapLoader />;
  }

  const extraBounds = waypointPoints.map((p) => ({ lat: p.lat, lng: p.lng }));

  return (
    <DistanceRouteMap
      pings={pings}
      extraBounds={extraBounds}
      onTimeChange={setIndicatorTime}
    >
      <WaypointMarkers points={waypointPoints} />
      {movingSitePosition && movingSitePosition.radius && (
        <Circle
          center={movingSitePosition.position}
          radius={movingSitePosition.radius}
          options={MOVING_SITE_CIRCLE_OPTIONS}
        />
      )}
    </DistanceRouteMap>
  );
};

export default DistanceMapEntity;
